/**
 * Per-broker setup copy for Trade Integration modal.
 * summary = one line under the broker title; steps = numbered list in the Instructions dropdown.
 * Built per call so the copy names the current brand's app.
 */
import { getBrandIdentity } from '../../utils/brandIdentity';

export function getBrokerSetup(appName = getBrandIdentity().appName) {
  return {
    coinbase: {
      summary: 'Coinbase Advanced Trade API keys — Ed25519 (recommended) or ECDSA.',
      steps: [
        {
          title: 'Create an API key in Coinbase',
          bullets: [
            {
              text: 'Open your Coinbase API settings:',
              link: { href: 'https://www.coinbase.com/settings/api', label: 'coinbase.com/settings/api' },
            },
            'Create a new API key with View (read-only) permission.',
            'Pick a signature type: Ed25519 is the new Coinbase-recommended format; ECDSA (EC) is the legacy format.',
            'Copy the API key name/ID and the private key (the secret).',
          ],
        },
        {
          title: `Connect in ${appName}`,
          bullets: [
            'Choose the matching key type with the toggle below.',
            'Paste the API Key Name and API Secret. Ed25519 secrets are a single base64 line; ECDSA secrets are a PEM block including the BEGIN/END lines.',
            'Click Connect & Sync — we pull your spot fills and estimate account value from trades.',
          ],
        },
        {
          title: 'Optional: exact history via CSV',
          bullets: [
            'Export your full transaction history CSV from Coinbase.',
            'Use Upload CSV here — it overrides the API estimate with accurate daily balance.',
          ],
        },
      ],
    },
    alpaca: {
      summary: 'Alpaca API key and secret (paper or live).',
      steps: [
        {
          title: 'Get keys from Alpaca',
          bullets: [
            'Log in to app.alpaca.markets → Paper or Live → API Keys.',
            'Create a key pair and copy the Key ID and Secret.',
          ],
        },
        {
          title: `Connect in ${appName}`,
          bullets: [
            'Paste API Key and API Secret below.',
            'Check Paper trading account if this is a paper account.',
            'Click Connect & Sync — we use Alpaca daily portfolio history for the growth chart.',
          ],
        },
      ],
    },
    ibkr: {
      summary: 'IBKR Flex token and Activity Flex Query ID.',
      steps: [
        {
          title: 'Create an Activity Flex Query (IBKR Client Portal)',
          bullets: [
            'Performance & Reports → Flex Queries → Create under Activity Flex Query.',
            'Enable sections: Trades and Net Asset Value (NAV) in Base.',
            'Set Date Period (e.g. Last 365 Calendar Days) and Save.',
            'Note the numeric Query ID shown for your query.',
          ],
        },
        {
          title: 'Enable Flex Web Service and copy your token',
          bullets: [
            'Flex Queries → Flex Web Service Configuration.',
            'Turn Flex Web Service ON and Save.',
            'Copy the full Current Token (or Generate New Token if needed).',
          ],
        },
        {
          title: `Connect in ${appName}`,
          bullets: [
            'Paste Flex Token and Flex Query ID below.',
            `Click Connect & Sync — ${appName} pulls the report automatically; you do not run the query manually in IBKR.`,
          ],
        },
      ],
      note: 'If the account has no trades yet (common on paper), connection still succeeds with 0 synced trades. Place a trade or widen the query date range, then Sync again.',
    },
    ninjatrader: {
      summary: 'Tradovate OAuth or API credentials (same account you use for NinjaTrader futures).',
      steps: [
        {
          title: 'Connect with OAuth (recommended)',
          bullets: [
            'In Add / Sync Trades → Ninja Trader, choose Demo or Live, then click Connect with Tradovate OAuth.',
            `Sign in on Tradovate and approve access — ${appName} syncs your fills automatically.`,
          ],
        },
        {
          title: 'Or use API key credentials',
          bullets: [
            'Log in at trader.tradovate.com → Application Settings → Add-Ons → subscribe to API Access ($25/mo; live funded account required).',
            'Open the API Access tab → Generate API Key → copy the CID and Secret.',
            `Enter your Tradovate username, password, API Key ID (CID), and API Secret in ${appName}.`,
            'Check Demo account if this is a sim account (uncheck for live).',
          ],
        },
      ],
      note: 'OAuth uses the app\'s server credentials (TRADOVATE_CLIENT_ID/SECRET). API keys are per-customer Tradovate credentials.',
    },
  };
}
